import { FC } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import Markdown from "markdown-to-jsx";
import { ReleaseNote, ReleaseNoteTag, localized } from "./releaseNotes";

const TAG_KEY: Record<ReleaseNoteTag, string> = {
  new: "tagNew",
  improved: "tagImproved",
  fixed: "tagFixed",
};

interface ReleaseNoteItemProps {
  note: ReleaseNote;
  /** "card" = teaser (summary only); "full" = title + summary + Markdown details. */
  variant?: "card" | "full";
}

const ReleaseNoteItem: FC<ReleaseNoteItemProps> = ({ note, variant = "card" }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const date = note.date ? format(parseISO(note.date), "d MMM yyyy") : null;
  const summary = localized(note.summary, lang);
  const title = localized(note.title, lang);
  const details = localized(note.details, lang);
  const isFull = variant === "full";

  return (
    <div
      className="release-note"
      data-testid="release-note"
      id={isFull ? `release-v${note.version}` : undefined}
    >
      <div className="release-note__head">
        <span className="release-note__ver">v{note.version}</span>
        {note.tag && (
          <span className={`release-note__tag release-note__tag--${note.tag}`}>
            {t(TAG_KEY[note.tag])}
          </span>
        )}
        {date && <span className="release-note__date">{date}</span>}
      </div>

      {isFull && title && <h2 className="release-note__title">{title}</h2>}

      {summary && <p className="release-note__body">{summary}</p>}

      {isFull && details && (
        <div className="release-note__details">
          <Markdown>{details}</Markdown>
        </div>
      )}
    </div>
  );
};

export default ReleaseNoteItem;
